'use client';
import { AnimatePresence, motion } from 'framer-motion';
import type { Session } from 'next-auth';
import Link from 'next/link';

import { LogoIcon, LogoTextIcon } from '@/components/icons';
import { SearchInput } from '@/components/search-input';
import { BooksIcon } from '../icons/books';
import { UserMenu } from './user-menu';
import { isProdState } from '@/constants';
import { MY_LIBRARY_PATH } from '@/constants/my-library-route';
import { NotificationsBell } from '@/features/notifications/components/notifications-bell';
import type { UserData } from '@/hooks/use-profile';

interface MobileHeaderProps {
  isSearchActive: boolean;
  onSearchStateChange: (isActive: boolean) => void;
  isAuthenticated: boolean;
  userData: UserData | null;
  user: Session['user'] | undefined;
  onOpenAuthDialog: () => void;
  onToggleMobileMenu: () => void;
  onLogout: () => void;
}

export function MobileHeader({
  isSearchActive,
  onSearchStateChange,
  isAuthenticated,
  userData,
  user,
  onOpenAuthDialog,
  onToggleMobileMenu,
  onLogout,
}: MobileHeaderProps) {
  return (
    <div className="flex lg:hidden items-center justify-between w-full">
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <Link href="/" className="flex items-center flex-shrink-0">
          <LogoIcon className="w-8 h-8 max-sm:w-6.5 max-sm:h-7 bg-[#25282e] rounded-md flex items-center justify-center" />
          <LogoTextIcon className="text-2xl font-bold text-white pb-1 max-sm:w-[75px] max-sm:h-7" />
        </Link>

        <div className="flex-1 min-w-0">
          <SearchInput
            placeholder="Поиск..."
            variant="mobile"
            onSearchStateChange={onSearchStateChange}
          />
        </div>
      </div>

      <AnimatePresence>
        {!isSearchActive && (
          <motion.div
            className="flex items-center space-x-2 flex-shrink-0 ml-3"
            initial={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
          >
            {isAuthenticated ? (
              <>
                <NotificationsBell />
                {!isProdState && (
                  <Link
                    href={MY_LIBRARY_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-[#25282E] hover:bg-[#4a4f57] rounded-lg flex items-center justify-center transition-colors"
                    title="Моя библиотека"
                    aria-label="Моя библиотека"
                  >
                    <BooksIcon className="w-4 h-4 text-white" />
                  </Link>
                )}
                <button
                  onClick={onToggleMobileMenu}
                  className="w-10 h-10 bg-[#25282E] rounded-lg flex items-center justify-center transition-colors"
                >
                  <div className="flex flex-col space-y-1 h-2.5">
                    <div className="w-3 h-0.5 bg-white rounded" />
                    <div className="w-3 h-0.5 bg-white rounded" />
                    <div className="w-3 h-0.5 bg-white rounded" />
                  </div>
                </button>
                <UserMenu userData={userData} user={user} onLogout={onLogout} />
              </>
            ) : (
              <>
                <button
                  className="bg-[#a8ea3a] hover:opacity-80 transition-opacity text-[#25282e] font-medium px-6 py-2.5 rounded-lg text-sm whitespace-nowrap min-w-[100px]"
                  onClick={onOpenAuthDialog}
                >
                  Войти
                </button>
                <button
                  onClick={onToggleMobileMenu}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                >
                  <div className="flex flex-col space-y-1">
                    <div className="w-5 h-0.5 bg-white rounded"></div>
                    <div className="w-5 h-0.5 bg-white rounded"></div>
                    <div className="w-5 h-0.5 bg-white rounded"></div>
                  </div>
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
